<script setup>
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { ErrorMessage, Field } from "vee-validate";
import { ref, watch, useId } from "vue";

const {
  fieldId,
  fieldName,
  required = false,
  label,
  enable = true,
} = defineProps({
  fieldId: {
    type: String,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    required: false,
  },
  label: {
    type: String,
    required: true,
  },
  enable: {
    type: Boolean,
    required: false,
  },
});

const model = defineModel();

const quillRef = ref(null);

const containerRef = ref(null);

const html = ref("");

const showHtml = ref(false);

const id = useId();

function init() {
  quillRef.value.getQuill().enable(enable);
  quillRef.value.setContents(getCleanedContent(model.value));
}

function getCleanedContent(content) {
  try {
    const parser = new DOMParser();
    const contentDom = parser.parseFromString(content, "text/html");
    const contentText = contentDom.body.innerText.replaceAll(/\s/g, "");

    contentDom.querySelectorAll("ul > *:not(li)").forEach((child) => {
      let item = child;

      do {
        item = item.previousSiblingElement;
      } while (item && !item.matches("li"));

      if (!item) {
        const list = child.parentNode;
        item = list.querySelector("li:last-of-type");
      }

      if (item) {
        item.appendChild(child);
      } else {
        list.removeChild(child);
      }
    });

    contentDom.querySelectorAll("p").forEach((p) => {
      if (p.innerHTML.trim() === "") {
        p.remove();
      }
    });

    contentDom.querySelectorAll("li > p").forEach((p) => {
      const li = p.parentNode;
      li.innerHTML = p.innerHTML;
    });

    let html =
      contentText !== ""
        ? contentDom.body.innerHTML &&
          contentDom.body.innerHTML !== "<p>null</p>" &&
          contentDom.body.innerHTML !== "null"
          ? contentDom.body.innerHTML
          : ""
        : "";

    html = html.replace(/\n/g, "");

    return html;
  } catch (error) {
    return content;
  }
}

function beautifyHtml(html) {
  let tab = "\t";
  let result = "";
  let indent = "";

  html.split(/>\s*</).forEach(function (element) {
    if (element.match(/^\/\w/)) {
      indent = indent.substring(tab.length);
    }

    result += indent + "<" + element + ">\r\n";

    if (element.match(/^<?\w[^>]*[^\/]$/) && !element.startsWith("input")) {
      indent += tab;
    }
  });

  return result.substring(1, result.length - 3);
}

function toggleHtml() {
  if (showHtml.value) {
    showHtml.value = false;
    model.value = getCleanedContent(html.value);
  } else {
    html.value = beautifyHtml(model.value);
    showHtml.value = true;
  }
}

watch(model, () => {
  model.value = getCleanedContent(model.value);
});

watch(html, () => {
  model.value = getCleanedContent(html.value);
});
</script>

<template>
  <div class="mb-4 flex flex-col">
    <label :for="fieldName" class="mb-2">
      {{ label }}
      <sup v-if="required" class="text-red-500">*</sup>
    </label>

    <Field
      v-model="model"
      :id="fieldId"
      :name="fieldName"
      class="rounded border border-gray-300 px-5 py-3 placeholder:text-gray-400 focus:border-transparent focus:ring-gray-500 disabled:cursor-not-allowed disabled:bg-gray-100"
    >
      <div
        ref="containerRef"
        class="quill-container"
        :class="{ hidden: showHtml }"
      >
        <QuillEditor
          theme="snow"
          :content="model"
          @update:content="(content) => (model = getCleanedContent(content))"
          content-type="html"
          :enable="enable"
          ref="quillRef"
          @ready="init()"
          :toolbar="'#custom-toolbar-' + id"
        >
          <template #toolbar>
            <div :id="'custom-toolbar-' + id">
              <span class="ql-formats">
                <select class="ql-header">
                  <option value="1">h1</option>
                  <option value="2">h2</option>
                  <option value="3">h3</option>
                  <option value="4">h4</option>
                  <option value="5">h5</option>
                  <option value="6">h6</option>
                  <option value="">p</option>
                </select>
              </span>

              <span class="ql-formats">
                <select class="ql-align"></select>
              </span>

              <span class="ql-formats">
                <button class="ql-bold"></button>
                <button class="ql-italic"></button>
                <button class="ql-underline"></button>
                <button class="ql-strike"></button>
              </span>

              <span class="ql-formats">
                <button class="ql-list" value="ordered"></button>
                <button class="ql-list" value="bullet"></button>
              </span>

              <span class="ql-formats">
                <button class="ql-indent" value="-1"></button>
                <button class="ql-indent" value="+1"></button>
              </span>

              <span class="ql-formats">
                <select class="ql-color"></select>
                <select class="ql-background"></select>
              </span>

              <span class="ql-formats">
                <button class="ql-blockquote"></button>
                <button class="ql-code-block"></button>
              </span>

              <span class="ql-formats">
                <button class="ql-link"></button>
                <button class="ql-image"></button>
              </span>

              <span class="ql-formats">
                <button @click="toggleHtml" type="button" class="!w-[6rem]">
                  HTML
                </button>
              </span>
            </div>
          </template>
        </QuillEditor>
        <div class="resizer"></div>
      </div>

      <div :class="{ hidden: !showHtml }">
        <div
          class="rounded-t border border-l border-r border-t border-gray-300 p-2"
        >
          <button
            type="button"
            class="box-border h-[2.4rem] px-[.5rem] py-[.3rem] hover:bg-[#F3F4F6]"
            @click="toggleHtml"
          >
            WYSIWYG
          </button>
        </div>

        <textarea
          v-model="html"
          class="min-h-[30rem] w-full rounded-b border border-gray-300 px-5 py-3 ring-1 ring-transparent duration-150 focus:outline-0 focus:ring-gray-300"
        ></textarea>
      </div>
    </Field>

    <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
  </div>
</template>

<style scoped>
:deep(.quill-container) {
  position: relative;
  width: 100%;
  border: 1px solid rgb(209 213 219);
  border-radius: 0.25rem;
}

:deep(.quill-container) .ql-editor {
  min-height: 30rem;
  max-height: 40rem;
  overflow-y: auto;
  resize: vertical;
}

:deep(.ql-toolbar.ql-snow) {
  border: 0;
  border-bottom: 1px solid rgb(209 213 219);
}

:deep(.ql-container.ql-snow) {
  border: 0;
}
</style>
